import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { getVectorStoreId, getWorkerApiUrl } from '../../api';
import useSessionContext from '../../hooks/useSessionContext';
import { createDataset, uploadDatasetFile } from '../../services/datasetsApi';
import { CloudArrowUpIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Button, Input, Select, SwitchToggle } from '../../ui/atoms';

const CreateFileDataset = () => {
  const { t } = useTranslation();
  const [datasetName, setDatasetName] = useState('');
  const [datasetType, setDatasetType] = useState('');
  const [visibilityTargets, setVisibilityTargets] = useState(['chat', 'articles']);
  const [files, setFiles] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  // Enhanced loading states
  const [processedFiles, setProcessedFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [progress, setProgress] = useState({
          stage: '', // stages: validation, uploading, vectorstore, dataset, complete
    message: '',
    currentFile: '',
    filesCompleted: 0,
    totalFiles: 0
  });
  
  const navigate = useNavigate();
  const { token, userId, workspaceId: sessionWorkspaceId, refreshContext } = useSessionContext();


  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

  const updateProgress = (stage, message, currentFile = '', filesCompleted = 0, totalFiles = 0) => {
    setProgress({
      stage,
      message,
      currentFile,
      filesCompleted,
      totalFiles
    });
  };

  const handleDrop = (acceptedFiles, rejectedFiles) => {
    // Reset previous errors
    setError('');
    
    // Handle rejected files
    if (rejectedFiles && rejectedFiles.length > 0) {
      const errors = rejectedFiles.reduce((acc, file) => {
        file.errors.forEach(err => {
          if (err.code === 'file-too-large') {
            acc.tooLarge.push(file.file.name);
          } else if (err.code === 'file-invalid-type') {
            acc.invalidType.push(file.file.name);
          }
        });
        return acc;
      }, { tooLarge: [], invalidType: [] });

      if (errors.tooLarge.length > 0) {
        setError(t('datasets.create.file.errors.tooLarge', { files: errors.tooLarge.join(', ') }));
        return;
      }
      if (errors.invalidType.length > 0) {
        setError(t('datasets.create.file.errors.invalidTypes', { files: errors.invalidType.join(', ') }));
        return;
      }
    }
    
    // Validate total number of files
    const totalFiles = files.length + acceptedFiles.length;
    if (totalFiles > 20) {
      setError(t('datasets.create.file.errors.tooMany'));
      return;
    }
    
    // Add accepted files
    if (acceptedFiles.length > 0) {
      setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      setError(''); // Clear any previous errors
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({ 
    onDrop: handleDrop,
    accept: {
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'application/json': ['.json'],
      'text/html': ['.html'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    noClick: true, // Disable click to open file dialog
    noKeyboard: true // Disable keyboard interaction
  });

  const handleRemoveFile = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const setVisibilityTarget = (target, enabled) => {
    setVisibilityTargets((prev) => {
      const hasTarget = prev.includes(target);
      if (enabled && !hasTarget) return [...prev, target];
      if (!enabled && hasTarget) return prev.filter((item) => item !== target);
      return prev;
    });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!datasetName.trim()) {
      errors.datasetName = t('datasets.create.file.form.nameRequired');
    }
    
    if (datasetName.length > 100) {
      errors.datasetName = 'Dataset name must be less than 100 characters';
    }
    
    if (!datasetType) {
      errors.datasetType = t('datasets.create.file.form.typeRequired');
    }
    if (visibilityTargets.length === 0) {
      errors.visibilityTargets = 'Select at least one availability target';
    }
    
    if (files.length === 0) {
      errors.files = 'At least one file is required';
    }
    
    if (files.length > 20) {
      errors.files = 'Maximum 20 files allowed';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setProcessedFiles([]);
    setCurrentFileIndex(0);

    try {
      // Stage 1: Validation
      updateProgress('validation', t('datasets.create.file.progress.validation'), '', 0, files.length);
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for UX

      if (window.location.hostname === 'localhost') {
        console.log('Starting file upload process...');
      }

      const fileIds = [];
      const vectorStoreFileIds = [];
      
      // Stage 2: Get Vector Store ID
      updateProgress('vectorstore', t('datasets.create.file.progress.vectorstore'), '', 0, files.length);
      const vectorStoreId = await getVectorStoreId();
      if (window.location.hostname === 'localhost') {
        console.log('Using vector store ID:', vectorStoreId);
      }

      // Stage 3: Upload files
      updateProgress('uploading', t('datasets.create.file.progress.uploading'), '', 0, files.length);
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentFileIndex(i);
        updateProgress('uploading', t('datasets.create.file.progress.uploading'), t('datasets.create.file.progress.file', { name: file.name }), i, files.length);
        
        if (window.location.hostname === 'localhost') {
          console.log(`Processing file ${i + 1}/${files.length}:`, file.name);
        }
        
        try {
          // Check token first
          if (!token) {
            throw new Error(t('datasets.create.file.errors.noAuthToken', { defaultValue: 'No authentication token found. Please log in again.' }));
          }

          if (window.location.hostname === 'localhost') {
            console.log('🔑 Using token:', token.substring(0, 20) + '...');
            console.log('📤 Uploading to Directus files endpoint');
          }

          const fileResponse = await uploadDatasetFile(token, file, 'a449f44e-926b-4940-9f46-0f5017223089');
          const directusFileId = fileResponse.id;
          fileIds.push(directusFileId);
          
          if (window.location.hostname === 'localhost') {
            console.log('File uploaded to Directus with ID:', directusFileId);
          }

          // Upload to OpenAI
          const openAIFormData = new FormData();
          openAIFormData.append('file', file);
          openAIFormData.append('purpose', 'assistants');

          // Upload to OpenAI via worker
          const apiUrl = getWorkerApiUrl('/api/files');

          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: openAIFormData
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const responseData = await response.json();
          
          if (!responseData.success) {
            throw new Error(responseData.error || 'Failed to upload file to OpenAI');
          }

          const openAIFileResponse = {
            data: {
              id: responseData.fileId,
              filename: responseData.filename
            }
          };

          const openAIFileId = openAIFileResponse.data.id;
          vectorStoreFileIds.push(openAIFileId);
          
          if (window.location.hostname === 'localhost') {
            console.log('File uploaded to OpenAI with ID:', openAIFileId);
          }

          // Add to processed files for UI feedback
          setProcessedFiles(prev => [...prev, {
            name: file.name,
            directusId: directusFileId,
            openAIId: openAIFileId,
            status: 'uploaded'
          }]);

        } catch (fileError) {
          throw new Error(`Failed to upload ${file.name}: ${fileError.message}`);
        }
      }

      // Stage 4: Create Vector Store Batch
      updateProgress('vectorstore', t('datasets.create.file.progress.creatingVectorBatch', { defaultValue: 'Creating AI vector store batch...' }), '', files.length, files.length);
      
      if (window.location.hostname === 'localhost') {
        console.log('All files uploaded. Creating vector store batch...');
      }
      
      // Use worker endpoint for vector store batch creation
      const apiUrl = getWorkerApiUrl('/api/vector-store-batch');

      const batchResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          vectorStoreId: vectorStoreId,
          fileIds: vectorStoreFileIds,
          chunkingStrategy: {
            type: 'static',
            static: {
              max_chunk_size_tokens: 800,
              chunk_overlap_tokens: 400
            }
          }
        })
      });

      if (!batchResponse.ok) {
        throw new Error(`HTTP error! status: ${batchResponse.status}`);
      }

      const batchData = await batchResponse.json();
      
      if (!batchData.success) {
        throw new Error(batchData.error || 'Failed to create vector store batch');
      }

      // Convert to axios-like response format for compatibility
      const batchResponseFormatted = {
        data: batchData.data
      };

      if (window.location.hostname === 'localhost') {
        console.log('Vector store batch created:', batchData.data);
      }

      // Stage 5: Create Dataset
      updateProgress('dataset', t('datasets.create.file.progress.creatingDataset', { defaultValue: 'Creating dataset in database...' }), '', files.length, files.length);
      
      // Get workspace ID (from localStorage or API)
      let workspaceId = sessionWorkspaceId;
      if (!workspaceId) {
        const refreshedContext = await refreshContext();
        workspaceId = refreshedContext.workspaceId;
      }
      if (!workspaceId) {
        throw new Error(t('datasets.create.shared.errors.workspaceRequired', { defaultValue: 'Unable to determine workspace ID' }));
      }
      
      const datasetData = {
        name: datasetName.trim(),
        status: 'published',
        type: datasetType,
        owner: userId,
        linked_workspace: workspaceId,
        data_sets: visibilityTargets,
        file: fileIds.length > 0 ? {
          create: fileIds.map(id => ({ directus_files_id: id })),
          delete: []
        } : undefined,
        vector_store_batch_id: batchResponseFormatted.data.id,
        dataset_type: 'file_upload',
        openai_file_ids: vectorStoreFileIds // Add OpenAI file IDs for deletion
      };

      if (window.location.hostname === 'localhost') {
        console.log('Creating dataset in Directus with data:', datasetData);
      }

      const datasetResponse = await createDataset(token, datasetData);

      if (window.location.hostname === 'localhost') {
        console.log('Dataset created successfully:', datasetResponse);
      }

      // Stage 6: Complete
      updateProgress('complete', t('datasets.create.file.progress.created', { defaultValue: 'Dataset created successfully!' }), '', files.length, files.length);
      
      // Brief pause to show completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      navigate('/datasets');
      
    } catch (error) {
      if (window.location.hostname === 'localhost') {
        console.error('Error:', error.response?.data || error);
      }
      setError(`${t('datasets.create.shared.errors.createFailed', { defaultValue: 'Failed to create dataset' })}: ${error.response?.data?.errors?.[0]?.message || error.message}`);
      updateProgress('error', t('datasets.create.shared.errors.createFailed', { defaultValue: 'Failed to create dataset' }), '', 0, 0);
    } finally {
      setLoading(false);
    }
  };

  const renderProgressBar = () => {
    if (!isLoading) return null;

    const progressPercentage = progress.totalFiles > 0 
      ? Math.round((progress.filesCompleted / progress.totalFiles) * 100) 
      : 0;

    return (
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-blue-900">
            {progress.stage === 'validation' && t('datasets.create.file.progress.validation')}
            {progress.stage === 'uploading' && t('datasets.create.file.progress.uploading')}
            {progress.stage === 'vectorstore' && t('datasets.create.file.progress.vectorstore')}
            {progress.stage === 'dataset' && t('datasets.create.file.progress.dataset')}
            {progress.stage === 'complete' && t('datasets.create.file.progress.complete')}
            {progress.stage === 'error' && t('datasets.create.file.progress.error')}
          </h4>
          {progress.totalFiles > 0 && (
            <span className="text-sm text-blue-700">
              {t('datasets.create.file.progress.filesProgress', { completed: progress.filesCompleted, total: progress.totalFiles })}
            </span>
          )}
        </div>
        
        <p className="text-sm text-blue-800 mb-3">{progress.message}</p>
        
        {progress.currentFile && (
          <p className="text-xs text-blue-600 mb-2">
            {progress.currentFile}
          </p>
        )}
        
        {progress.totalFiles > 0 && (
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        )}
      </div>
    );
  };

  const renderFileList = () => {
    if (files.length === 0) return null;

    return (
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">{t('datasets.create.file.dropzone.selectedFiles', { count: files.length })}</h4>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {files.map((file, index) => {
            const processed = processedFiles.find(pf => pf.name === file.name);
            const isProcessing = isLoading && currentFileIndex === index;
            
            return (
              <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0">
                    {processed?.status === 'uploaded' ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : isProcessing ? (
                      <div className="h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <div className="h-5 w-5 border border-gray-300 rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                {!isLoading && (
                  <button
                    type="button"
                    className="text-red-600 hover:text-red-800 text-sm ml-2"
                    onClick={() => handleRemoveFile(file)}
                  >
                    {t('datasets.create.file.dropzone.remove')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {t('datasets.create.file.dropzone.totalSize', { size: formatFileSize(files.reduce((total, file) => total + file.size, 0)) })}
        </p>
      </div>
    );
  };

  return (
    <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
      <div className="px-4 py-8 sm:p-8">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <label htmlFor="dataset-name" className="block text-sm font-medium leading-6 text-gray-900">
                {t('datasets.create.file.form.name')}
              </label>
              <div className="mt-2">
                <Input
                  type="text"
                  placeholder="i.e. My Amazing Dataset"
                  id="dataset-name"
                  value={datasetName}
                  onChange={(e) => setDatasetName(e.target.value)}
                  disabled={isLoading}
                  required
                  error={Boolean(formErrors.datasetName)}
                  className="!py-2.5 disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>
              {formErrors.datasetName && <span className="text-red-500 text-sm">{formErrors.datasetName}</span>}
            </div>

            <div className="sm:col-span-6">
              <label className="block text-sm font-medium leading-6 text-gray-900">
                {t('datasets.create.shared.availableIn', { defaultValue: 'Available in' })}
              </label>
              <div className="mt-3 space-y-3">
                <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-gray-900">
                      {t('datasets.create.shared.visibility.chat.title', { defaultValue: 'Chat / Assistant' })}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('datasets.create.shared.visibility.chat.description', {
                        defaultValue: 'Enable if you want this dataset to be visible and used in Assistant.'
                      })}
                    </p>
                  </div>
                  <SwitchToggle
                    checked={visibilityTargets.includes('chat')}
                    onChange={(checked) => !isLoading && setVisibilityTarget('chat', checked)}
                    label={t('datasets.create.shared.visibility.chat.toggleAria', { defaultValue: 'Chat visibility' })}
                    className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                  />
                </div>
                <div className="flex items-start justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
                  <div className="pr-4">
                    <p className="text-sm font-medium text-gray-900">
                      {t('datasets.create.shared.visibility.articles.title', { defaultValue: 'Articles' })}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t('datasets.create.shared.visibility.articles.description', {
                        defaultValue: 'Enable if you want this dataset used while generating articles.'
                      })}
                    </p>
                  </div>
                  <SwitchToggle
                    checked={visibilityTargets.includes('articles')}
                    onChange={(checked) => !isLoading && setVisibilityTarget('articles', checked)}
                    label={t('datasets.create.shared.visibility.articles.toggleAria', { defaultValue: 'Articles visibility' })}
                    className={isLoading ? 'opacity-50 pointer-events-none' : ''}
                  />
                </div>
              </div>
              {formErrors.visibilityTargets && <span className="text-red-500 text-sm">{formErrors.visibilityTargets}</span>}
            </div>

            <div className="sm:col-span-6">
              <label htmlFor="dataset-type" className="block text-sm font-medium leading-6 text-gray-900">
                {t('datasets.create.file.form.type')}
              </label>
              <Select
                required
                name="dataset-type"
                id="dataset-type"
                value={datasetType}
                onChange={(e) => setDatasetType(e.target.value)}
                disabled={isLoading}
                className="form-select disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t('datasets.create.file.form.selectType')}</option>
                <option value="product_brochure">{t('datasets.dataTypes.product_brochure')}</option>
                <option value="blog_post_article">{t('datasets.dataTypes.blog_post_article')}</option>
                <option value="technical_documentation">{t('datasets.dataTypes.technical_documentation')}</option>
                <option value="database">{t('datasets.dataTypes.database')}</option>
                <option value="other">{t('datasets.dataTypes.other')}</option>
              </Select>
              {formErrors.datasetType && <span className="text-red-500 text-sm">{formErrors.datasetType}</span>}
            </div>

            <div className="col-span-full">
              <label htmlFor="files" className="block text-sm font-medium leading-6 text-gray-900">
                {t('datasets.create.file.dropzone.dragAndDrop')}
              </label>
              <div
                {...getRootProps()}
                className={`mt-2 flex justify-center rounded-lg border border-dashed px-6 py-10 w-full transition-colors ${
                  isDragActive 
                    ? 'bg-primary-50 border-primary-300' 
                    : isLoading
                    ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
                    : 'border-gray-900/25 hover:bg-gray-50'
                }`}
              >
                <div className="text-center">
                  <CloudArrowUpIcon className={`mx-auto h-12 w-12 ${isLoading ? 'text-gray-200' : 'text-gray-300'}`} aria-hidden="true" />
                  <div className="mt-4 flex text-sm leading-6 text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className={`relative cursor-pointer rounded-md bg-white font-semibold ${
                        isLoading 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : 'text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-600 focus-within:ring-offset-2'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={open}
                        className="text-primary-600 hover:text-primary-500 focus:outline-none focus:underline"
                        disabled={isLoading}
                      >
                        {isLoading ? t('datasets.create.file.dropzone.processing') : t('datasets.create.file.dropzone.upload')}
                      </button>
                      {!isLoading && <input {...getInputProps()} />}
                    </label>
                    <p className="pl-1">{isLoading ? '' : t('datasets.create.file.dropzone.dragAndDrop')}</p>
                  </div>
                  <p className="text-xs leading-5 text-gray-600">
                    {isLoading 
                      ? t('datasets.create.file.dropzone.processing')
                      : t('datasets.create.file.dropzone.fileInfo')
                    }
                  </p>
                </div>
              </div>
              
              {renderFileList()}
              {formErrors.files && <span className="text-red-500 text-sm">{formErrors.files}</span>}
            </div>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
                <span className="text-red-800 text-sm">{error}</span>
              </div>
            </div>
          )}

          {renderProgressBar()}

          <div className="mt-6 flex items-center justify-end gap-x-6">
            <Button
              type="submit"
              disabled={isLoading || files.length === 0}
              variant="primary"
              size="md"
              className="disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating Dataset...' : 'Create File Dataset'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFileDataset; 